import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import * as Dialog from "@radix-ui/react-dialog";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  useAccount,
  useChainId,
  useConnect,
  useDisconnect,
  useSwitchChain,
  useBalance,
  type Connector,
} from "wagmi";
import { mainnet } from "wagmi/chains";
import { formatUnits } from "viem";
import { explorerAddressUrl } from "@/lib/contracts";

function shortAddr(a: string): string {
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

// Native ETH, three decimals, grouped thousands. "—" before it loads.
function fmtEth(v: bigint | undefined): string {
  if (v === undefined) return "—";
  return Number(formatUnits(v, 18)).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
}

// A friendlier label than the raw connector name for the generic case.
function prettyName(c: Connector): string {
  if (c.name === "Injected") return "Browser wallet";
  return c.name;
}

type WalletUI = { openConnect: () => void; closeConnect: () => void };
const Ctx = createContext<WalletUI | null>(null);

export function useWalletUI(): WalletUI {
  const c = useContext(Ctx);
  if (!c) throw new Error("useWalletUI must be used within WalletUIProvider");
  return c;
}

export function WalletUIProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const openConnect = useCallback(() => setOpen(true), []);
  const closeConnect = useCallback(() => setOpen(false), []);
  const value = useMemo(() => ({ openConnect, closeConnect }), [openConnect, closeConnect]);
  return (
    <Ctx.Provider value={value}>
      {children}
      <ConnectDialog open={open} onOpenChange={setOpen} />
    </Ctx.Provider>
  );
}

// The default mark for a wallet with no icon of its own.
function WalletGlyph({ connector }: { connector: Connector }) {
  if (connector.icon) {
    return <img className="wc-opt-icon" src={connector.icon} alt="" aria-hidden="true" />;
  }
  return (
    <span className="wc-opt-icon wc-opt-icon--fallback" aria-hidden="true">
      {connector.name.slice(0, 1)}
    </span>
  );
}

function ConnectDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { connectors, connect, isPending, variables, error } = useConnect();

  // wagmi can surface the same wallet twice (a static connector plus its
  // EIP-6963 discovered twin). Keep the first of each name.
  const list = useMemo(() => {
    const seen = new Set<string>();
    const out: Connector[] = [];
    for (const c of connectors) {
      const key = c.name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
    return out;
  }, [connectors]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="wc-overlay" />
        <Dialog.Content className="wc-modal" aria-describedby={undefined}>
          <div className="wc-head">
            <Dialog.Title className="wc-title">Connect a wallet</Dialog.Title>
            <Dialog.Close className="wc-x" aria-label="Close">
              ×
            </Dialog.Close>
          </div>
          <div className="wc-list">
            {list.map((c) => {
              const pending = variables?.connector;
              const busy =
                isPending && !!pending && "uid" in pending && pending.uid === c.uid;
              return (
                <button
                  key={c.uid}
                  className="wc-opt"
                  disabled={isPending}
                  onClick={() =>
                    connect(
                      { connector: c },
                      { onSuccess: () => onOpenChange(false) },
                    )
                  }
                >
                  <WalletGlyph connector={c} />
                  <span className="wc-opt-name">{prettyName(c)}</span>
                  {busy ? <span className="wc-opt-sub">connecting…</span> : null}
                </button>
              );
            })}
          </div>
          {error ? <p className="wc-err">{error.message}</p> : null}
          <p className="wc-note">
            Your wallet stays in your control. 01 never holds your keys. Transactions run on
            Ethereum mainnet.
          </p>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// The header wallet control: connect, switch network, or the connected
// account menu (copy address, view on explorer, disconnect).
export function WalletButton() {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const wrongNet = isConnected && chainId !== mainnet.id;
  const { openConnect } = useWalletUI();
  const { disconnect } = useDisconnect();
  const { switchChain } = useSwitchChain();
  const { data: bal } = useBalance({
    address,
    query: { enabled: Boolean(address), refetchInterval: 15_000 },
  });

  if (!isConnected) {
    return (
      <button className="wallet" onClick={openConnect}>
        Connect wallet
      </button>
    );
  }

  if (wrongNet) {
    return (
      <button className="wallet wrong" onClick={() => switchChain({ chainId: mainnet.id })}>
        <span className="dot" />
        Switch to Ethereum
      </button>
    );
  }

  const addr = address as string;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="wallet">
          <span className="dot" />
          {shortAddr(addr)}{" "}
          <span className="net">{bal ? `${fmtEth(bal.value)} ETH` : "Ethereum"}</span>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="wc-menu" align="end" sideOffset={6}>
          <div className="wc-menu-head">
            <span className="wc-menu-addr mono">{shortAddr(addr)}</span>
            <span className="wc-menu-bal mono">{bal ? `${fmtEth(bal.value)} ETH` : ""}</span>
          </div>
          <DropdownMenu.Item
            className="wc-menu-item"
            onSelect={() => {
              navigator.clipboard?.writeText(addr).catch(() => {});
            }}
          >
            Copy address
          </DropdownMenu.Item>
          <DropdownMenu.Item className="wc-menu-item" asChild>
            <a href={explorerAddressUrl(addr)} target="_blank" rel="noreferrer">
              View on Etherscan
            </a>
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="wc-menu-sep" />
          <DropdownMenu.Item
            className="wc-menu-item wc-menu-item--danger"
            onSelect={() => disconnect()}
          >
            Disconnect
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
